import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { fetchAdminStatus, type StatusView } from "../../services/http.js";

/**
 * Is anything stuck?
 *
 * Counts, not graphs. The question this answers is the one somebody asks when a
 * deck looks wrong at nine on a Saturday — *has the catalog got anything in it,
 * and is the queue moving* — and until now it was answerable only with `psql` on
 * the box.
 *
 * Nothing here is per-party or per-player: a live game is not a curator's
 * business, and a list of them would be a surveillance surface built to answer
 * an operational question that a single number answers just as well.
 */
export function StatusPanel() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<StatusView | null>(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void fetchAdminStatus().then(
      (loaded) => {
        if (!cancelled) {
          setStatus(loaded);
        }
      },
      () => {
        if (!cancelled) {
          setMissing(true);
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, []);

  if (missing) {
    return <p className="text-sm text-white/50">{t("admin.nothingHere")}</p>;
  }
  if (!status) {
    return <p className="text-sm text-white/50">{t("admin.status.counting")}</p>;
  }

  return (
    <div className="flex flex-col gap-5">
      {/* `row.key` is the server's own enum value — `verified`, `probable`,
          `queued` — and is left as it comes: these are the names the CLI, the
          logs and the SQL use, so translating them would leave a curator
          matching a Spanish word against an English column. */}
      <Group label={t("admin.status.catalog")}>
        {status.tracks_by_confidence.map((row) => (
          <Line key={row.key} label={row.key} value={row.count} />
        ))}
        <Line label={t("admin.status.withClip")} value={status.with_audio} />
        <Line label={t("admin.status.suspended")} value={status.suspended} />
        <Line label={t("admin.status.removed")} value={status.removed} />
      </Group>

      <Group label={t("admin.status.queue")}>
        {status.jobs_by_status.length === 0 ? (
          <p className="text-sm text-white/40">{t("admin.status.empty")}</p>
        ) : (
          status.jobs_by_status.map((row) => (
            <Line key={row.key} label={row.key} value={row.count} />
          ))
        )}
      </Group>

      <Group label={t("admin.status.rightNow")}>
        <Line label={t("admin.status.waiting")} value={status.queue_waiting} />
        <Line label={t("admin.status.parties")} value={status.live_parties} />
      </Group>
    </div>
  );
}

function Group({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section aria-label={label}>
      <h2 className="mb-1 text-xs uppercase tracking-wide text-white/40">{label}</h2>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-3">
        {children}
      </dl>
    </section>
  );
}

function Line({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="text-white/50">{label}</dt>
      <dd className="tabular-nums">{value}</dd>
    </div>
  );
}
